/**
 * advanceForm.ts — what the admin has typed, between the inputs and the writer.
 *
 * THE FORM HOLDS STRINGS AND THE WRITER TAKES FIGURES, and this module is the one place the two
 * meet. `Number('')` IS 0, which is the hazard the whole file exists for: a weekly repayment of
 * nought written as a figure is an advance that claims a schedule and bills nothing, and a rate box
 * an admin cleared is not an interest-free advance they chose. Empty parses to `null` here, and
 * `null` means "not provided" everywhere downstream (`AdvanceDraft`, `validateDraft`).
 *
 * THE EDITS ARE A PATCH OVER THE SERVER'S VALUES, NEVER A COPY OF THEM. A form seeded once from the
 * read and then owned by the page goes stale the moment anything refetches — the shape that makes a
 * background refresh silently discard a figure an admin typed, or worse, re-assert a figure they
 * cleared. What is held is only what was TOUCHED, so an untouched field follows the server and a
 * touched one does not, which is the inline reducer's rule 1 applied to a form the page owns.
 *
 * THE TWO FIELD GROUPS ARE THE TWO AREAS, and they are declared here because the commit bars are
 * per area: the disbursement's three fields commit together, the repayment's two commit together,
 * and neither bar may claim a field the other area holds.
 *
 * PURE, AND REACT-FREE.
 */

import type { AdvanceDraft } from './advanceCommit';
import type { AdvanceArea } from './advanceSteps';

export interface AdvanceFormValues {
    /** `loans.transaction_id` — picked, never typed. */
    transactionId: number | null;
    /** The picked disbursement's amount, as the selector resolved it. Read, never written. */
    amount: number | null;
    /** `loans.interest_rate`, as typed. An empty box writes 0 — the column is NOT NULL. */
    interestRate: string;
    /** `loans.description` — the Schedule's `{{loan.purpose}}`. */
    purpose: string;
    /** `loan_instalments.amount` — the weekly repayment, as typed. */
    weeklyAmount: string;
    /** `loan_instalments.starts_at` — a CALENDAR day, `'yyyy-MM-dd'`, never an instant. */
    firstStartsAt: string;
}

export type AdvanceField = keyof AdvanceFormValues;

/** The saved advance, as the data hook reads it back. */
export interface AdvanceSavedValues {
    transactionId: number | null;
    amount: number | null;
    interestRate: number | null;
    purpose: string | null;
    weeklyAmount: number | null;
    firstStartsAt: string | null;
}

/** Which area's bar owns which fields — see the head. */
export const AREA_FIELDS: Record<AdvanceArea, AdvanceField[]> = {
    disbursement: ['transactionId', 'amount', 'interestRate', 'purpose'],
    repayment: ['weeklyAmount', 'firstStartsAt'],
    agreement: [],
};

/**
 * The server's values in the form's vocabulary — what an untouched field shows.
 *
 * A NUMBER PRINTS AS ITSELF AND A NULL PRINTS AS EMPTY. `String(0)` is `'0'`, which is right: an
 * interest-free advance has a rate of zero on file and the box has to show it, or an admin saving
 * an unrelated correction would write their empty box over a deliberate figure.
 */
export function advanceFormFromSaved(saved: AdvanceSavedValues): AdvanceFormValues {
    return {
        transactionId: saved.transactionId,
        amount: saved.amount,
        interestRate: saved.interestRate === null ? '' : String(saved.interestRate),
        purpose: saved.purpose ?? '',
        weeklyAmount: saved.weeklyAmount === null ? '' : String(saved.weeklyAmount),
        firstStartsAt: saved.firstStartsAt ?? '',
    };
}

/** The server's values with whatever was typed over the top. */
export function advanceFormWith(
    saved: AdvanceSavedValues,
    edits: Partial<AdvanceFormValues>,
): AdvanceFormValues {
    return { ...advanceFormFromSaved(saved), ...edits };
}

/**
 * A figure, or nothing at all.
 *
 * EMPTY IS NOT ZERO, AND A NON-NUMBER IS NOT ZERO EITHER. `Number('abc')` is `NaN`, and a `NaN`
 * that reached PostgREST would be a refused insert at best; it answers null and the refusal list
 * then says the field is missing, which is what it is.
 */
export function parseAdvanceNumber(value: string | null | undefined): number | null {
    const raw = (value ?? '').trim();
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
}

/** The form as the writer takes it. */
export function advanceDraftOf(form: AdvanceFormValues): AdvanceDraft {
    return {
        transactionId: form.transactionId,
        amount: form.amount,
        interestRate: parseAdvanceNumber(form.interestRate),
        purpose: form.purpose.trim() ? form.purpose.trim() : null,
        weeklyAmount: parseAdvanceNumber(form.weeklyAmount),
        firstStartsAt: form.firstStartsAt.trim() ? form.firstStartsAt.trim() : null,
    };
}

/**
 * Which of this area's fields have actually MOVED — never merely which were touched.
 *
 * A FIELD TYPED BACK TO WHAT IT WAS IS NOT AN EDIT. The bar is what tells an admin there is
 * something unsaved on the page, so a bar standing over a form identical to the server's is the
 * control crying wolf — and the Discard beside it would appear to do nothing.
 */
export function advanceAreaDirty(
    area: AdvanceArea,
    saved: AdvanceSavedValues,
    edits: Partial<AdvanceFormValues>,
): boolean {
    const base = advanceFormFromSaved(saved);
    return AREA_FIELDS[area].some(
        (field) => field in edits && edits[field] !== base[field],
    );
}
